import {
    calculationRequestFilterOptions as buildCalculationRequestFilterOptions,
    calculationRequestTableDescriptor,
} from '@/components/features/CalculationRequests/tables/calculation-request-table-descriptor';
import { CalculationRequestTable } from '@/components/features/CalculationRequests/tables/CalculationRequestTable';
import type { CalculationRequest, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import {
    companyFilterOptions as buildCompanyFilterOptions,
    companyOptionSourcesFromRows,
    companyTableDescriptor,
} from '@/components/features/Companies/tables/company-table-descriptor';
import { CompanyCard } from '@/components/features/Companies/tables/CompanyCard';
import { CompanyTable } from '@/components/features/Companies/tables/CompanyTable';
import EmployeeOverview, {
    resolveEmployeeRoleView,
    type EmployeeReport,
    type EmployeeRoleView,
    type EmployeeStats,
} from '@/components/features/Employees/components/EmployeeOverview';
import NotificationSettingsTab from '@/components/features/Employees/components/NotificationSettingsTab';
import PermissionsTab from '@/components/features/Employees/components/PermissionsTab';
import AssignCompany from '@/components/features/Employees/forms/AssignCompany';
import EditEmployee from '@/components/features/Employees/forms/EditEmployee';
import TransferEmployeeModal, { type TransferCounts } from '@/components/features/Employees/forms/TransferEmployeeModal';
import {
    leadFilterOptions as buildLeadFilterOptions,
    leadOptionSourcesFromRows,
    leadTableDescriptor,
} from '@/components/features/Leads/tables/lead-table-descriptor';
import { LeadCard } from '@/components/features/Leads/tables/LeadCard';
import { LeadTable } from '@/components/features/Leads/tables/LeadTable';
import {
    projectFilterOptions as buildProjectFilterOptions,
    projectOptionSourcesFromRows,
    projectTableDescriptor,
} from '@/components/features/Projects/tables/project-table-descriptor';
import { ProjectCard } from '@/components/features/Projects/tables/ProjectCard';
import { ProjectTable } from '@/components/features/Projects/tables/ProjectTable';
import {
    taskFilterOptions as buildTaskFilterOptions,
    taskOptionSourcesFromRows,
    taskTableDescriptor,
} from '@/components/features/Projects/tables/task-table-descriptor';
import { TaskCard } from '@/components/features/Projects/tables/TaskCard';
import { TaskTable } from '@/components/features/Projects/tables/TaskTable';
import { WorkOrderCard } from '@/components/features/WorkOrders/components/WorkOrderCard';
import { WorkOrderTable } from '@/components/features/WorkOrders/tables/WorkOrderTable';
import { ResponsiveActions, type ResponsiveActionItem } from '@/components/global/ResponsiveActions';
import { EmbeddedResourceTable } from '@/components/ui/embedded-resource-table/EmbeddedResourceTable';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useSettings } from '@/contexts/SettingsContext';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile, useIsTabletOrMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import type { AssignableUser } from '@/lib/assigned-user-options';
import axios from '@/lib/axios';
import { ProjectStatusOption } from '@/lib/constants-db';
import { cn, logger, useCan } from '@/lib/utils';
import { Company, Lead, PriorityOption, Project, ProjectTask, StatusOption, WorkOrder, type BreadcrumbItem, type User as UserType } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import {
    ArrowRightLeft,
    Building2,
    Calculator,
    CheckSquare,
    FolderOpen,
    MoreHorizontal,
    Settings,
    ShieldCheck,
    SquarePen,
    Trash2,
    TrendingUp,
    User,
    UserPlus,
    Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { route } from 'ziggy-js';

interface ShowUserPageProps {
    user: UserType;
    companies: Company[];
    leads: Lead[];
    owned_projects: Project[];
    assigned_tasks: ProjectTask[];
    work_orders?: WorkOrder[];
    approvedEmailDomains?: string[];
    transferCounts?: TransferCounts;
    allUsers?: { uuid: string; first_name: string; last_name: string }[];
    /** Calculations assigned to this employee — engineers only, in practice. */
    calculation_requests?: CalculationRequest[];
    /** Badge catalogues and the engineer list the calculations table renders with. */
    calculationRequestTableOptions?: Pick<CalculationRequestOptions, 'statuses' | 'phases' | 'complexities' | 'engineers'>;
    canViewCalculationFinancials?: boolean;
    /** Counts the page cannot derive from the records above (contacts, delivery team). */
    employeeStats?: EmployeeStats;
    /** Colleagues whose effective manager is this employee. */
    reports?: EmployeeReport[];
    manager?: { id: string; first_name?: string | null; last_name?: string | null } | null;
    /** Every active colleague, with their roles — what the people filters group by. */
    filterUsers?: AssignableUser[];
    /** Every company, so the tabs can filter by one that is not on the current page. */
    filterCompanies?: Array<{ id: string; name: string }>;
    [key: string]: unknown;
}

/**
 * Every task on this page is already this employee's, so the assignee column and
 * its filter would both answer a question nobody is asking here.
 */
const TASK_TAB_HIDDEN_IDS = ['assignee'] as const;

interface EmployeeTab {
    value: string;
    label: string;
    displayLabel: ReactNode;
    count?: number;
    content: ReactNode;
}

type CompanyUser = {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
};

type CompanyWithMeta = Company & {
    users?: CompanyUser[];
    total_profit?: number;
    total_people?: number;
};

export default function ShowUser() {
    const { props } = usePage<ShowUserPageProps>();
    const {
        user,
        companies,
        leads,
        owned_projects,
        assigned_tasks,
        work_orders,
        calculation_requests,
        calculationRequestTableOptions,
        canViewCalculationFinancials,
        approvedEmailDomains,
        transferCounts,
        allUsers,
        employeeStats,
        reports,
        manager,
        filterUsers = [],
        filterCompanies = [],
    } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();
    const isMobile = useIsMobile();
    const isTabletOrMobile = useIsTabletOrMobile();
    const userCompanies = (companies || []) as CompanyWithMeta[];
    const ownedProjects = owned_projects || [];
    // Memoised, unlike its neighbours: the tasks tab derives filter options and a
    // tab list from it, and a fresh array each render would rebuild both every time.
    const assignedTasks = useMemo(() => assigned_tasks || [], [assigned_tasks]);
    const workOrders = work_orders || [];
    const calculationRequests = calculation_requests || [];
    const { can } = useCan();
    const [workOrdersViewMode, setWorkOrdersViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [workOrdersSearchValue, setWorkOrdersSearchValue] = useState('');
    const debouncedWorkOrdersSearch = useDebounce(workOrdersSearchValue, 350);
    const [workOrdersColumnFilterSearch, setWorkOrdersColumnFilterSearch] = useState<Record<string, string>>({});
    const [workOrdersSortColumn, setWorkOrdersSortColumn] = useState<string | null>(null);
    const [workOrdersSortDirection, setWorkOrdersSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [workOrdersVisibleColumns, setWorkOrdersVisibleColumns] = useState<Set<string>>(
        new Set(['work_order', 'contact', 'service_address', 'company', 'assigned_to', 'type', 'status', 'priority', 'estimated_date']),
    );
    const [workOrdersColumnFilters, setWorkOrdersColumnFilters] = useState<Record<string, string[]>>({});
    const [workOrdersFilterOpen, setWorkOrdersFilterOpen] = useState(false);
    const [workOrdersSortOpen, setWorkOrdersSortOpen] = useState(false);
    const [workOrdersPage, setWorkOrdersPage] = useState(1);
    const [workOrdersPerPage, setWorkOrdersPerPage] = useState(10);
    const [assignableUsers, setAssignableUsers] = useState<UserType[]>([]);

    /**
     * The whole page — overview panel and tab set alike — follows the role this
     * employee holds: a service worker has no business being shown a leads tab,
     * and a sales manager has no service board to run.
     */
    const roleView = useMemo(() => resolveEmployeeRoleView(user.role as string | null | undefined), [user.role]);

    useEffect(() => {
        const fetchAssignableUsers = async () => {
            try {
                const response = await axios.get('/api/users/assignable');
                const nextUsers = Array.isArray(response.data?.users) ? (response.data.users as UserType[]) : [];
                setAssignableUsers(nextUsers);
            } catch {
                setAssignableUsers([]);
            }
        };

        void fetchAssignableUsers();
    }, []);

    useEffect(() => {
        // The migrated tabs follow the viewport inside EmbeddedResourceTable.
        setWorkOrdersViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    /**
     * Filter options for the migrated tabs. The columns, filters and sort entries
     * come from the resource descriptors — so these tables match their index pages
     * exactly — and only the option *sources* are local. Where the server ships a
     * reference list (filterUsers, filterCompanies) it wins over the rows on
     * screen, which could only ever offer values already visible.
     */
    const companyFilterOptions = useMemo(() => {
        const fromRows = companyOptionSourcesFromRows(userCompanies);

        return buildCompanyFilterOptions({
            t,
            // Settings hold the full domain of types and categories; the rows only
            // ever cover what is already on screen.
            types: settings.companyTypes || [],
            categories: settings.companyCategories || fromRows.categories,
            priorities: settings.companyPriorities || [],
            users: filterUsers.length > 0 ? filterUsers : fromRows.users,
        });
    }, [t, settings.companyTypes, settings.companyCategories, settings.companyPriorities, userCompanies, filterUsers]);

    const leadFilterOptions = useMemo(() => {
        const fromRows = leadOptionSourcesFromRows(leads ?? []);

        return buildLeadFilterOptions({
            t,
            statuses: (settings.leadStatuses as unknown as StatusOption[]) || [],
            priorities: (settings.leadPriorities as unknown as PriorityOption[]) || [],
            companies: filterCompanies.length > 0 ? filterCompanies : fromRows.companies,
            owners: filterUsers.length > 0 ? filterUsers : fromRows.owners,
        });
    }, [t, settings.leadStatuses, settings.leadPriorities, leads, filterUsers, filterCompanies]);

    const projectFilterOptions = useMemo(() => {
        const fromRows = projectOptionSourcesFromRows(ownedProjects);

        return buildProjectFilterOptions({
            t,
            statuses: (settings.projectStatuses as unknown as ProjectStatusOption[]) || [],
            years: fromRows.years,
            companies: filterCompanies.length > 0 ? filterCompanies : fromRows.companies,
            users: filterUsers.length > 0 ? filterUsers : fromRows.users,
        });
    }, [t, settings.projectStatuses, ownedProjects, filterUsers, filterCompanies]);

    const taskFilterOptions = useMemo(() => {
        const fromRows = taskOptionSourcesFromRows(assignedTasks);

        return buildTaskFilterOptions({
            t,
            statuses: settings.projectTaskStatuses || [],
            projects: fromRows.projects,
            users: filterUsers.length > 0 ? filterUsers : fromRows.users,
        });
    }, [t, settings.projectTaskStatuses, assignedTasks, filterUsers]);

    const companyTableUpdates = useMemo(
        () => ({
            priority: async (companyId: string, value: string) => {
                router.patch(`/company/${companyId}/priority`, { priority: value }, { preserveState: true, preserveScroll: true });
            },
            type: async (companyId: string, value: string) => {
                router.patch(`/company/${companyId}`, { type: value }, { preserveState: true, preserveScroll: true });
            },
        }),
        [],
    );

    const leadTableUpdates = useMemo(
        () => ({
            status: async (leadId: string, value: string) => {
                router.patch(`/leads/${leadId}/status`, { status: value }, { preserveState: true, preserveScroll: true });
            },
            priority: async (leadId: string, value: string) => {
                router.patch(`/leads/${leadId}/priority`, { priority: value }, { preserveState: true, preserveScroll: true });
            },
        }),
        [],
    );

    const projectTableUpdates = useMemo(
        () => ({
            status: async (projectId: string, value: string) => {
                router.patch(`/projects/${projectId}/status`, { status: value }, { preserveState: true, preserveScroll: true });
            },
        }),
        [],
    );

    const workOrdersColumnDefinitions = useMemo(
        () => [
            { id: 'work_order', label: t('work_orders.index.columns.work_order', 'Work Order') },
            { id: 'contact', label: t('work_orders.index.columns.contact', 'Contact') },
            { id: 'service_address', label: t('work_orders.index.columns.service_address', 'Service Address') },
            { id: 'company', label: t('work_orders.index.columns.company', 'Company') },
            { id: 'assigned_to', label: t('work_orders.index.columns.assigned_to', 'Assigned To') },
            { id: 'type', label: t('work_orders.index.columns.type', 'Type') },
            { id: 'status', label: t('work_orders.index.columns.status', 'Status') },
            { id: 'priority', label: t('work_orders.index.columns.priority', 'Priority') },
            { id: 'estimated_date', label: t('work_orders.index.columns.estimated_date', 'Estimated Date') },
        ],
        [t],
    );

    const workOrdersQuickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('work_orders.index.quick_filters.status', 'Status') },
            { id: 'type', label: t('work_orders.index.quick_filters.type', 'Type') },
            { id: 'priority', label: t('work_orders.index.quick_filters.priority', 'Priority') },
        ],
        [t],
    );

    const workOrdersColumnFilterOptions = useMemo(() => {
        const status = settings.workOrderStatuses.map((s) => ({ value: s.value, label: s.label }));
        const priority = settings.workOrderPriorities.map((p) => ({ value: p.value, label: p.label }));
        const type = settings.workOrderTypes.map((wt) => ({ value: wt.value, label: wt.label }));
        const companyMap = new Map<string, string>();
        const assignedMap = new Map<string, string>();

        workOrders.forEach((workOrder) => {
            if (workOrder.company) {
                companyMap.set(workOrder.company.id, workOrder.company.name);
            }
            const assignments = workOrder.assignments || [];
            assignments.forEach((assignment) => {
                const user = assignment.user;
                if (user) {
                    assignedMap.set(user.id, `${user.first_name || ''} ${user.last_name || ''}`.trim());
                }
            });
        });

        return {
            status,
            priority,
            type,
            company: Array.from(companyMap.entries()).map(([value, label]) => ({ value, label })),
            assigned_to: Array.from(assignedMap.entries()).map(([value, label]) => ({ value, label })),
        } as Record<string, Array<{ value: string; label: string }>>;
    }, [settings.workOrderPriorities, settings.workOrderStatuses, settings.workOrderTypes, workOrders]);

    const filteredWorkOrders = useMemo(() => {
        const q = debouncedWorkOrdersSearch.trim().toLowerCase();
        let filtered = workOrders.filter((workOrder) => {
            if (q) {
                const contactName = `${workOrder.contact_first_name || ''} ${workOrder.contact_last_name || ''}`.trim();
                const companyName = workOrder.company?.name || '';
                const woNumber = workOrder.wo_number || '';
                const matches = [woNumber, contactName, companyName, workOrder.service_address || ''].join(' ').toLowerCase().includes(q);
                if (!matches) return false;
            }

            return Object.entries(workOrdersColumnFilters).every(([column, values]) => {
                if (!values.length) return true;
                if (column === 'status') return values.includes(workOrder.status || '');
                if (column === 'priority') return values.includes(workOrder.priority || '');
                if (column === 'type') return values.includes(workOrder.type || '');
                if (column === 'company') return workOrder.company ? values.includes(workOrder.company.id) : false;
                if (column === 'assigned_to') {
                    const ids = (workOrder.assignments || []).map((assignment) => assignment.user?.id).filter(Boolean) as string[];
                    return values.some((value) => ids.includes(value));
                }
                return true;
            });
        });

        if (workOrdersSortColumn && workOrdersSortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const valFor = (workOrder: WorkOrder): string | number => {
                    if (workOrdersSortColumn === 'work_order') return workOrder.wo_number || workOrder.id;
                    if (workOrdersSortColumn === 'contact')
                        return `${workOrder.contact_first_name || ''} ${workOrder.contact_last_name || ''}`.trim();
                    if (workOrdersSortColumn === 'service_address') return workOrder.service_address || '';
                    if (workOrdersSortColumn === 'company') return workOrder.company?.name || '';
                    if (workOrdersSortColumn === 'assigned_to') {
                        const first = workOrder.assignments?.[0]?.user;
                        return first ? `${first.first_name || ''} ${first.last_name || ''}`.trim() : '';
                    }
                    if (workOrdersSortColumn === 'type') return workOrder.type || '';
                    if (workOrdersSortColumn === 'status') return workOrder.status || '';
                    if (workOrdersSortColumn === 'priority') return workOrder.priority || '';
                    if (workOrdersSortColumn === 'estimated_date') return workOrder.estimated_date ? new Date(workOrder.estimated_date).getTime() : 0;
                    return '';
                };
                const av = valFor(a);
                const bv = valFor(b);
                const result = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
                return workOrdersSortDirection === 'asc' ? result : -result;
            });
        }

        return filtered;
    }, [debouncedWorkOrdersSearch, workOrders, workOrdersColumnFilters, workOrdersSortColumn, workOrdersSortDirection]);

    const workOrdersPagination = useMemo(() => {
        const total = filteredWorkOrders.length;
        const lastPage = Math.max(1, Math.ceil(total / workOrdersPerPage));
        const currentPage = Math.min(workOrdersPage, lastPage);
        const from = total === 0 ? null : (currentPage - 1) * workOrdersPerPage + 1;
        const to = total === 0 ? null : Math.min(currentPage * workOrdersPerPage, total);
        return { current_page: currentPage, last_page: lastPage, per_page: workOrdersPerPage, total, from, to };
    }, [filteredWorkOrders.length, workOrdersPage, workOrdersPerPage]);

    const paginatedWorkOrders = useMemo(() => {
        const start = (workOrdersPagination.current_page - 1) * workOrdersPagination.per_page;
        return filteredWorkOrders.slice(start, start + workOrdersPagination.per_page);
    }, [filteredWorkOrders, workOrdersPagination.current_page, workOrdersPagination.per_page]);

    const workOrdersQuickFilterNames = workOrdersQuickFilterDefinitions
        .filter((qf) => (workOrdersColumnFilters[qf.id] || []).length > 0)
        .map((qf) => qf.label);
    const workOrdersQuickFilterButtonLabel =
        workOrdersQuickFilterNames.length > 0
            ? `${t('work_orders.index.toolbar.quick_filters', 'Quick filters')}: ${workOrdersQuickFilterNames.join(', ')}`
            : t('work_orders.index.filters.all_work_orders', 'All Work Orders');

    const workOrdersTableUpdates = useMemo(
        () => ({
            estimatedDate: async (workOrderId: string, value: string | null) => {
                router.patch(`/work-orders/${workOrderId}`, { estimated_date: value || null }, { preserveState: true, preserveScroll: true });
            },
            priority: async (workOrderId: string, value: string) => {
                router.patch(`/work-orders/${workOrderId}/priority`, { priority: value }, { preserveState: true, preserveScroll: true });
            },
            status: async (workOrderId: string, value: string) => {
                router.patch(`/work-orders/${workOrderId}/status-editable`, { status: value }, { preserveState: true, preserveScroll: true });
            },
            type: async (workOrderId: string, value: string) => {
                router.patch(`/work-orders/${workOrderId}/details`, { work_type: value }, { preserveState: true, preserveScroll: true });
            },
        }),
        [],
    );

    const handleDelete = () => {
        router.delete(`/employees/${user.id}`, {
            onSuccess: () => {
                logger.log('Delete successful, redirecting...');
                // Redirect to employees index after successful deletion
                router.visit(route('employees'));
            },
            onError: (errors) => {
                logger.error('Delete failed with errors:', errors);
            },
        });
    };

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('employees.show.breadcrumbs.employees', 'Employees'),
            href: '/employees',
        },
        {
            title: `${user.first_name} ${user.last_name}`,
            href: route('employees.show', { employee: user.id }),
        },
    ];

    /**
     * The calculations tab's options. Engineers are a closed list — only they can
     * hold a calculation — while a request is usually raised by sales, so the
     * created-by side reads the full colleague list.
     */
    const calculationTableOptions = useMemo(
        () => ({
            statuses: calculationRequestTableOptions?.statuses ?? [],
            phases: calculationRequestTableOptions?.phases ?? [],
            complexities: calculationRequestTableOptions?.complexities ?? [],
            engineers: calculationRequestTableOptions?.engineers ?? [],
        }),
        [calculationRequestTableOptions],
    );

    const calculationFilterOptions = useMemo(
        () =>
            buildCalculationRequestFilterOptions({
                t,
                statuses: calculationTableOptions.statuses,
                phases: calculationTableOptions.phases,
                complexities: calculationTableOptions.complexities,
                // Fixed vocabulary, unlike statuses and phases — see
                // App\Enums\CalculationRequestOutcome, which is deliberately not admin-managed.
                outcomes: ['pending', 'won', 'lost'],
                engineers: calculationTableOptions.engineers,
                submitters: filterUsers,
                companies: filterCompanies,
            }),
        [t, calculationTableOptions, filterUsers, filterCompanies],
    );

    const tabs = useMemo(() => {
        const formatTabLabel = (icon: ReactNode, label: string, count?: number): ReactNode => (
            <span className="inline-flex items-center gap-1.5">
                {icon}
                <span>{label}</span>
                {count !== undefined && <span className="text-xs">({count})</span>}
            </span>
        );

        const baseTabs: EmployeeTab[] = [
            {
                value: 'details',
                label: t('employees.show.tabs.details', 'Details'),
                displayLabel: formatTabLabel(<User className="h-4 w-4" />, t('employees.show.tabs.details', 'Details')),
                content: (
                    <EmployeeOverview
                        user={user}
                        roleView={roleView}
                        companies={userCompanies}
                        leads={leads}
                        projects={ownedProjects}
                        tasks={assignedTasks}
                        workOrders={workOrders}
                        calculationRequests={calculationRequests}
                        calculationStatuses={calculationRequestTableOptions?.statuses ?? []}
                        stats={employeeStats ?? { contacts: 0, team_size: 0 }}
                        reports={reports ?? []}
                        manager={manager ?? null}
                    />
                ),
            },
            {
                value: 'companies',
                label: t('employees.show.tabs.companies', 'Companies'),
                count: userCompanies?.length || 0,
                displayLabel: formatTabLabel(
                    <Building2 className="h-4 w-4" />,
                    t('employees.show.tabs.companies', 'Companies'),
                    userCompanies?.length || 0,
                ),
                content: (
                    <EmbeddedResourceTable
                        descriptor={companyTableDescriptor}
                        rows={userCompanies}
                        storageKey="companies:employee-show"
                        filterOptions={companyFilterOptions}
                        emptyMessage={t('companies.index.empty.no_companies_found', 'No companies found.')}
                        renderCard={(company) => <CompanyCard company={company} />}
                        renderTable={({ rows, tableProps }) => (
                            <CompanyTable
                                {...tableProps}
                                companies={rows}
                                users={assignableUsers}
                                onUpdate={companyTableUpdates}
                                types={settings.companyTypes || []}
                                priorities={settings.companyPriorities || []}
                            />
                        )}
                    />
                ),
            },
            {
                value: 'leads',
                label: t('employees.show.tabs.leads', 'Leads'),
                count: leads?.length || 0,
                displayLabel: formatTabLabel(<TrendingUp className="h-4 w-4" />, t('employees.show.tabs.leads', 'Leads'), leads?.length || 0),
                content: (
                    <EmbeddedResourceTable
                        descriptor={leadTableDescriptor}
                        rows={leads ?? []}
                        storageKey="leads:employee-show"
                        filterOptions={leadFilterOptions}
                        emptyMessage={t('leads.index.empty.no_leads_found', 'No leads found.')}
                        renderCard={(lead) => <LeadCard lead={lead} />}
                        renderTable={({ rows, tableProps }) => (
                            <LeadTable
                                {...tableProps}
                                leads={rows}
                                onUpdate={leadTableUpdates}
                                statuses={(settings.leadStatuses as unknown as StatusOption[]) || []}
                                priorities={(settings.leadPriorities as unknown as PriorityOption[]) || []}
                                approvalThreshold={settings.leadOfferApprovalThreshold}
                            />
                        )}
                    />
                ),
            },
            {
                value: 'projects',
                label: t('employees.show.tabs.projects', 'Projects'),
                count: ownedProjects?.length || 0,
                displayLabel: formatTabLabel(
                    <FolderOpen className="h-4 w-4" />,
                    t('employees.show.tabs.projects', 'Projects'),
                    ownedProjects?.length || 0,
                ),
                content: (
                    <EmbeddedResourceTable
                        descriptor={projectTableDescriptor}
                        rows={ownedProjects}
                        storageKey="projects:employee-show"
                        filterOptions={projectFilterOptions}
                        emptyMessage={t('projects.index.empty.no_projects_found', 'No projects found.')}
                        renderCard={(project) => <ProjectCard project={project} />}
                        renderTable={({ rows, tableProps }) => (
                            <ProjectTable
                                {...tableProps}
                                projects={rows}
                                onUpdate={projectTableUpdates}
                                statuses={(settings.projectStatuses as unknown as ProjectStatusOption[]) || []}
                            />
                        )}
                    />
                ),
            },
            {
                value: 'calculations',
                label: t('employees.show.tabs.calculations', 'Calculations'),
                count: calculationRequests.length,
                displayLabel: formatTabLabel(
                    <Calculator className="h-4 w-4" />,
                    t('employees.show.tabs.calculations', 'Calculations'),
                    calculationRequests.length,
                ),
                content: (
                    <EmbeddedResourceTable
                        descriptor={calculationRequestTableDescriptor}
                        rows={calculationRequests}
                        storageKey="calculation-requests:employee-show"
                        filterOptions={calculationFilterOptions}
                        emptyMessage={t('calculation_requests.index.empty.title', 'No calculation requests')}
                        renderTable={({ rows, tableProps }) => (
                            <CalculationRequestTable
                                {...tableProps}
                                requests={rows}
                                options={calculationTableOptions}
                                canViewFinancials={canViewCalculationFinancials}
                                readOnly
                            />
                        )}
                    />
                ),
            },
            {
                value: 'tasks',
                label: t('employees.show.tabs.tasks', 'Tasks'),
                count: assignedTasks?.length || 0,
                displayLabel: formatTabLabel(
                    <CheckSquare className="h-4 w-4" />,
                    t('employees.show.tabs.tasks', 'Tasks'),
                    assignedTasks?.length || 0,
                ),
                content: (
                    <EmbeddedResourceTable
                        descriptor={taskTableDescriptor}
                        rows={assignedTasks}
                        storageKey="tasks:employee-show"
                        filterOptions={taskFilterOptions}
                        hiddenColumnIds={TASK_TAB_HIDDEN_IDS}
                        hiddenFilterIds={TASK_TAB_HIDDEN_IDS}
                        emptyMessage={t('projects.tables.tasks.empty', 'No tasks found.')}
                        renderCard={(task) => <TaskCard task={task} />}
                        renderTable={({ rows, tableProps }) => <TaskTable {...tableProps} tasks={rows} />}
                    />
                ),
            },
            {
                value: 'work-orders',
                label: t('employees.show.tabs.work_orders', 'Work Orders'),
                count: workOrders?.length || 0,
                displayLabel: formatTabLabel(
                    <Wrench className="h-4 w-4" />,
                    t('employees.show.tabs.work_orders', 'Work Orders'),
                    workOrders?.length || 0,
                ),
                content: (
                    <div className="space-y-4">
                        <GenericToolbar
                            quickFilterButtonLabel={workOrdersQuickFilterButtonLabel}
                            quickFilterDefinitions={workOrdersQuickFilterDefinitions}
                            columnDefinitions={workOrdersColumnDefinitions}
                            columnFilterOptions={workOrdersColumnFilterOptions}
                            columnFilters={workOrdersColumnFilters}
                            columnFilterSearch={workOrdersColumnFilterSearch}
                            setColumnFilterSearch={setWorkOrdersColumnFilterSearch}
                            toggleSingleFilter={(column, value) => {
                                const current = workOrdersColumnFilters[column] || [];
                                const nextValues = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
                                setWorkOrdersColumnFilters((previous) => ({ ...previous, [column]: nextValues }));
                                setWorkOrdersPage(1);
                            }}
                            handleFilterChange={(column, values) => {
                                setWorkOrdersColumnFilters((previous) => {
                                    const next = { ...previous };
                                    if (!values.length) delete next[column];
                                    else next[column] = values;
                                    return next;
                                });
                                setWorkOrdersPage(1);
                            }}
                            clearFilters={() => {
                                setWorkOrdersColumnFilters({});
                                setWorkOrdersSearchValue('');
                                setWorkOrdersPage(1);
                            }}
                            isFilterOpen={workOrdersFilterOpen}
                            setIsFilterOpen={setWorkOrdersFilterOpen}
                            isSortOpen={workOrdersSortOpen}
                            setIsSortOpen={setWorkOrdersSortOpen}
                            sortColumn={workOrdersSortColumn}
                            sortDirection={workOrdersSortDirection}
                            handleSortChange={(column, direction) => {
                                setWorkOrdersSortColumn(column);
                                setWorkOrdersSortDirection(direction);
                                setWorkOrdersPage(1);
                            }}
                            clearSort={() => {
                                setWorkOrdersSortColumn(null);
                                setWorkOrdersSortDirection(null);
                            }}
                            search={workOrdersSearchValue}
                            setSearch={setWorkOrdersSearchValue}
                            perPage={workOrdersPerPage}
                            onPerPageChange={(perPage) => {
                                setWorkOrdersPerPage(perPage);
                                setWorkOrdersPage(1);
                            }}
                            viewMode={workOrdersViewMode}
                            setViewMode={setWorkOrdersViewMode}
                            visibleColumns={workOrdersVisibleColumns}
                            handleColumnShow={(column) =>
                                setWorkOrdersVisibleColumns((previous) => {
                                    const next = new Set(previous);
                                    next.add(column);
                                    return next;
                                })
                            }
                            handleColumnHide={(column) =>
                                setWorkOrdersVisibleColumns((previous) => {
                                    const next = new Set(previous);
                                    next.delete(column);
                                    return next;
                                })
                            }
                            filteredCount={workOrdersPagination.total}
                            resourceName="work order"
                            resourceNamePlural="work orders"
                            sortableColumns={workOrdersColumnDefinitions}
                            excludeFromMoreFilters={['status', 'type', 'priority']}
                        />

                        {workOrdersViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {paginatedWorkOrders.map((workOrder) => (
                                    <WorkOrderCard key={workOrder.id} workOrder={workOrder} />
                                ))}
                            </div>
                        ) : (
                            <WorkOrderTable
                                workOrders={paginatedWorkOrders}
                                users={assignableUsers}
                                onUpdate={workOrdersTableUpdates}
                                sortColumn={workOrdersSortColumn}
                                sortDirection={workOrdersSortDirection}
                                onSortChange={(column, direction) => {
                                    setWorkOrdersSortColumn(column);
                                    setWorkOrdersSortDirection(direction);
                                    setWorkOrdersPage(1);
                                }}
                                onColumnHide={(column) =>
                                    setWorkOrdersVisibleColumns((previous) => {
                                        const next = new Set(previous);
                                        next.delete(column);
                                        return next;
                                    })
                                }
                                onColumnShow={(column) =>
                                    setWorkOrdersVisibleColumns((previous) => {
                                        const next = new Set(previous);
                                        next.add(column);
                                        return next;
                                    })
                                }
                                visibleColumns={workOrdersVisibleColumns}
                                columnFilters={workOrdersColumnFilters}
                                columnFilterOptions={workOrdersColumnFilterOptions}
                                onFilterChange={(column, values) => {
                                    setWorkOrdersColumnFilters((previous) => {
                                        const next = { ...previous };
                                        if (!values.length) delete next[column];
                                        else next[column] = values;
                                        return next;
                                    });
                                    setWorkOrdersPage(1);
                                }}
                                statuses={settings.workOrderStatuses}
                                priorities={settings.workOrderPriorities}
                                types={settings.workOrderTypes}
                            />
                        )}

                        <PaginationControls
                            pagination={workOrdersPagination}
                            onPageChange={setWorkOrdersPage}
                            onPerPageChange={(perPage) => {
                                setWorkOrdersPerPage(perPage);
                                setWorkOrdersPage(1);
                            }}
                            loading={false}
                            className="mt-6"
                        />

                        {workOrdersViewMode === 'grid' && paginatedWorkOrders.length === 0 && (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <p className="text-muted-foreground">
                                        {t('work_orders.index.empty.no_work_orders_found', 'No work orders found matching your filters.')}
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ),
            },
        ];

        /**
         * Each role works with its own subset of records — a service worker has
         * no leads board, a sales manager runs no service queue. A tab is only
         * dropped when the role has no use for it *and* the employee holds no
         * records in it, so nothing ever becomes unreachable.
         */
        const tabsForRole: Record<EmployeeRoleView, string[]> = {
            sales: ['details', 'companies', 'leads', 'projects', 'tasks', 'work-orders'],
            project_manager: ['details', 'companies', 'projects', 'tasks', 'work-orders'],
            service_manager: ['details', 'work-orders', 'tasks'],
            service_worker: ['details', 'work-orders', 'tasks'],
            engineer: ['details', 'calculations', 'projects', 'tasks'],
            generic: ['details', 'companies', 'leads', 'projects', 'tasks', 'work-orders'],
        };

        const roleTabs = baseTabs.filter((tab) => tabsForRole[roleView].includes(tab.value) || (tab.count ?? 0) > 0);

        if (can('acl.manage')) {
            roleTabs.push({
                value: 'permissions',
                label: t('employees.show.tabs.permissions', 'Permissions'),
                displayLabel: formatTabLabel(<ShieldCheck className="h-4 w-4" />, t('employees.show.tabs.permissions', 'Permissions')),
                content: <PermissionsTab userId={user.id} />,
            });
        }

        if (can('employees.update')) {
            roleTabs.push({
                value: 'settings',
                label: t('employees.show.tabs.settings', 'Settings'),
                displayLabel: formatTabLabel(<Settings className="h-4 w-4" />, t('employees.show.tabs.settings', 'Settings')),
                content: (
                    <div className="space-y-4">
                        <NotificationSettingsTab user={user} />
                    </div>
                ),
            });
        }

        return roleTabs;
    }, [
        userCompanies,
        companyFilterOptions,
        companyTableUpdates,
        assignableUsers,
        settings.companyTypes,
        settings.companyPriorities,
        leads,
        leadFilterOptions,
        leadTableUpdates,
        settings.leadStatuses,
        settings.leadPriorities,
        settings.leadOfferApprovalThreshold,
        ownedProjects,
        projectFilterOptions,
        projectTableUpdates,
        settings.projectStatuses,
        assignedTasks,
        taskFilterOptions,
        workOrdersViewMode,
        workOrdersSearchValue,
        workOrdersColumnFilterSearch,
        workOrdersSortColumn,
        workOrdersSortDirection,
        workOrdersVisibleColumns,
        workOrdersColumnFilters,
        workOrdersFilterOpen,
        workOrdersSortOpen,
        workOrdersPerPage,
        workOrdersColumnDefinitions,
        workOrdersQuickFilterDefinitions,
        workOrdersColumnFilterOptions,
        paginatedWorkOrders,
        workOrdersPagination,
        workOrdersQuickFilterButtonLabel,
        workOrdersTableUpdates,
        settings.workOrderStatuses,
        settings.workOrderPriorities,
        settings.workOrderTypes,
        calculationRequests,
        calculationTableOptions,
        calculationFilterOptions,
        canViewCalculationFinancials,
        employeeStats,
        manager,
        reports,
        roleView,
        can,
        t,
    ]);

    const [activeTab, setActiveTab] = useState<string>('details');
    const [mobileActionsDrawerOpen, setMobileActionsDrawerOpen] = useState(false);

    // The action modals used to be mounted three times over — once for the
    // desktop row, once for the mobile bar, once inside the actions drawer —
    // each with its own live form. They are now mounted once and driven from
    // here, which also lets the actions drawer finish closing before one of
    // them opens; two overlays at once leaves the page with a stuck
    // pointer-events lock.
    const [assignCompanyOpen, setAssignCompanyOpen] = useState(false);
    const [editEmployeeOpen, setEditEmployeeOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    type EmployeeAction = 'assign' | 'edit' | 'transfer' | 'delete';

    /**
     * Closes the actions drawer, then opens the chosen overlay once it has
     * animated out — mounting the second while the first is still leaving
     * stacks two overlays and leaves the body pointer-events locked. The wait
     * cannot hang off the drawer's onOpenChange: vaul only fires that for
     * closes it initiates itself.
     */
    const requestActionFromDrawer = (action: EmployeeAction) => {
        setMobileActionsDrawerOpen(false);

        window.setTimeout(() => {
            if (action === 'assign') setAssignCompanyOpen(true);
            if (action === 'edit') setEditEmployeeOpen(true);
            if (action === 'transfer') setTransferOpen(true);
            if (action === 'delete') setDeleteConfirmOpen(true);
        }, 350);
    };
    const maxVisibleTabs = isTabletOrMobile ? 3 : 4;
    const visibleTabs = tabs.slice(0, maxVisibleTabs);
    const overflowTabs = tabs.slice(maxVisibleTabs);
    const hasOverflow = overflowTabs.length > 0;

    const actionItems: ResponsiveActionItem[] = [
        ...(can('assign-employees')
            ? [
                  {
                      key: 'assign-company',
                      label: t('employees.forms.assign_company.buttons.assign_employee_to_company', 'Assign employee to company'),
                      icon: <UserPlus className="h-4 w-4" />,
                      onOverflowSelect: () => setAssignCompanyOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-2 whitespace-nowrap"
                              onClick={() => setAssignCompanyOpen(true)}
                          >
                              <UserPlus className="h-4 w-4" />
                              {t('employees.forms.assign_company.buttons.assign_employee_to_company', 'Assign employee to company')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        ...(can('employees.update')
            ? [
                  {
                      key: 'edit',
                      label: t('employees.forms.edit.buttons.edit_employee', 'Edit employee'),
                      icon: <SquarePen className="h-4 w-4" />,
                      onOverflowSelect: () => setEditEmployeeOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0"
                              onClick={() => setEditEmployeeOpen(true)}
                              aria-label={t('employees.forms.edit.buttons.edit_employee', 'Edit employee')}
                          >
                              <SquarePen className="h-4 w-4" />
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        ...(can('employees.transfer') && transferCounts && allUsers
            ? [
                  {
                      key: 'transfer',
                      label: t('employees.show.actions.transfer_data', 'Transfer Data'),
                      icon: <ArrowRightLeft className="h-4 w-4" />,
                      onOverflowSelect: () => setTransferOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0"
                              onClick={() => setTransferOpen(true)}
                              aria-label={t('employees.show.actions.transfer_data', 'Transfer Data')}
                          >
                              <ArrowRightLeft className="h-4 w-4" />
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        ...(can('employees.delete')
            ? [
                  {
                      key: 'delete',
                      label: t('employees.show.actions.delete', 'Delete Employee'),
                      icon: <Trash2 className="h-4 w-4" />,
                      destructive: true,
                      onOverflowSelect: () => setDeleteConfirmOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0"
                              onClick={() => setDeleteConfirmOpen(true)}
                              aria-label={t('employees.show.actions.delete', 'Delete Employee')}
                          >
                              <Trash2 className="h-4 w-4" />
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
    ];

    return (
        <>
            <Head title={t('employees.show.title', 'Employee Details')} />
            <ShowLayout
                breadcrumbs={breadcrumbs}
                title={
                    <span className="flex min-w-0 flex-wrap items-center gap-3">
                        <span className="truncate">
                            {user.first_name} {user.last_name}
                        </span>
                        {user.role ? (
                            <Badge variant="secondary" className="text-sm font-medium">
                                {String(user.role)}
                            </Badge>
                        ) : null}
                    </span>
                }
            >
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                    <div className="mb-6 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                        <div className="min-w-0 lg:flex-[3]">
                            {/* Same shape as the other detail pages: a scrolling row of
                                cards on small screens, the segmented list on desktop.
                                Wrapping the triggers pushed the row onto two lines. */}
                            {isTabletOrMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {[...visibleTabs, ...overflowTabs].map((tab) => {
                                            const isActive = activeTab === tab.value;

                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="text-sm leading-tight font-medium">{tab.displayLabel}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList
                                    className={cn(
                                        // max-w-full keeps a long tab set from spilling over the
                                        // action row that shares this line.
                                        'bg-muted flex h-auto max-w-full min-w-0 flex-1 flex-nowrap items-center gap-1 rounded-xl px-1 py-1 sm:w-fit sm:flex-initial',
                                    )}
                                >
                                    {visibleTabs.map((tab) => (
                                        <TabsTrigger key={tab.value} value={tab.value} className={cn('rounded-md text-sm')}>
                                            {tab.displayLabel}
                                        </TabsTrigger>
                                    ))}
                                    {hasOverflow && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'focus-visible:ring-ring inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                                        overflowTabs.find((x) => x.value === activeTab)
                                                            ? 'bg-secondary text-secondary-foreground'
                                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                                    )}
                                                >
                                                    {overflowTabs.find((x) => x.value === activeTab)
                                                        ? overflowTabs.find((x) => x.value === activeTab)?.displayLabel
                                                        : t('work_orders.show.tabs.more', '+{count} more').replace(
                                                              '{count}',
                                                              String(overflowTabs.length),
                                                          )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[150px]">
                                                {overflowTabs.map((tab) => (
                                                    <DropdownMenuItem
                                                        key={tab.value}
                                                        onClick={() => setActiveTab(tab.value)}
                                                        className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                                    >
                                                        {tab.displayLabel}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TabsList>
                            )}
                        </div>

                        <div className="hidden min-w-0 lg:flex lg:flex-[2]">
                            <ResponsiveActions className="w-full" items={actionItems} />
                        </div>
                    </div>

                    {tabs.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="mt-0 outline-none data-[state=inactive]:hidden">
                            {tab.content}
                        </TabsContent>
                    ))}
                </Tabs>
            </ShowLayout>

            {isTabletOrMobile && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur lg:hidden">
                    <div className="grid grid-cols-5 gap-2">
                        {can('assign-employees') ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setAssignCompanyOpen(true)}
                                aria-label={t('employees.forms.assign_company.buttons.assign_employee_to_company', 'Assign employee to company')}
                            >
                                <UserPlus className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        {can('employees.update') ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setEditEmployeeOpen(true)}
                                aria-label={t('employees.forms.edit.buttons.edit_employee', 'Edit employee')}
                            >
                                <SquarePen className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        {can('employees.delete') ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setDeleteConfirmOpen(true)}
                                aria-label={t('employees.show.actions.delete', 'Delete Employee')}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        {can('employees.transfer') && transferCounts && allUsers ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setTransferOpen(true)}
                                aria-label={t('employees.show.actions.transfer_data', 'Transfer Data')}
                            >
                                <ArrowRightLeft className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setMobileActionsDrawerOpen(true)}
                            aria-label={t('common.more_actions', 'More actions')}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
            <Drawer open={mobileActionsDrawerOpen} onOpenChange={setMobileActionsDrawerOpen} direction="bottom">
                <DrawerContent className="lg:hidden">
                    <DrawerHeader>
                        <DrawerTitle>{t('companies.sidebar.actions', 'Actions')}</DrawerTitle>
                        <DrawerDescription>{t('common.select_action', 'Select an action')}</DrawerDescription>
                    </DrawerHeader>
                    <div className="space-y-1 p-4 pt-0">
                        {can('assign-employees') && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('assign')}
                            >
                                <UserPlus className="h-4 w-4" />
                                {t('employees.forms.assign_company.buttons.assign_employee_to_company', 'Assign employee to company')}
                            </Button>
                        )}
                        {can('employees.update') && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('edit')}
                            >
                                <SquarePen className="h-4 w-4" />
                                {t('employees.forms.edit.buttons.edit_employee', 'Edit employee')}
                            </Button>
                        )}
                        {can('employees.transfer') && transferCounts && allUsers && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('transfer')}
                            >
                                <ArrowRightLeft className="h-4 w-4" />
                                {t('employees.show.actions.transfer_data', 'Transfer Data')}
                            </Button>
                        )}
                        {can('employees.delete') && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-destructive hover:text-destructive h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('delete')}
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('employees.show.actions.delete', 'Delete Employee')}
                            </Button>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>
            {isTabletOrMobile && <div className="h-24 lg:h-0" />}

            {/* Mounted once, driven by the desktop row, the mobile bar and the
                actions drawer alike. */}
            {can('assign-employees') && (
                <AssignCompany
                    companies={companies || []}
                    userCompanies={userCompanies}
                    userId={user.id}
                    open={assignCompanyOpen}
                    onOpenChange={setAssignCompanyOpen}
                />
            )}
            {can('employees.update') && (
                <EditEmployee
                    user={user}
                    approvedEmailDomains={approvedEmailDomains || []}
                    open={editEmployeeOpen}
                    onOpenChange={setEditEmployeeOpen}
                />
            )}
            {can('employees.transfer') && transferCounts && allUsers && (
                <TransferEmployeeModal
                    employeeId={user.id}
                    transferCounts={transferCounts}
                    allUsers={allUsers}
                    open={transferOpen}
                    onOpenChange={setTransferOpen}
                />
            )}

            <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('employees.show.actions.delete_title', 'Are you sure?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t(
                                'employees.show.actions.delete_description',
                                'This action cannot be undone. This will permanently delete the employee and remove all associated data from the database.',
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('employees.show.actions.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            {t('employees.show.actions.delete', 'Delete Employee')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
